import axios from 'axios'
import { getConfig } from '../config'
import { getStoredAuth, setStoredAuth, clearStoredAuth } from '../auth/storage'

// Instancia axios única. baseURL se setea en initApi() desde la config runtime.
export const api = axios.create()

// callback que la capa de auth puede registrar para reaccionar a un logout forzado
let onForcedLogout = null
export function setForcedLogoutHandler(fn) {
  onForcedLogout = fn
}

export function applyAuthHeaders(auth) {
  if (auth?.accessToken) {
    api.defaults.headers.common['Authorization'] = `Bearer ${auth.accessToken}`
  } else {
    delete api.defaults.headers.common['Authorization']
  }
  if (auth?.locacionId != null) {
    api.defaults.headers.common['X-Access'] = auth.locacionId
  } else {
    delete api.defaults.headers.common['X-Access']
  }
}

export function initApi() {
  const cfg = getConfig()
  api.defaults.baseURL = cfg.apiBase || '/qualita-client/rest'
  applyAuthHeaders(getStoredAuth())
}

// --- Interceptor de respuesta: refresh en 401, logout si falla ---
// El token Qualita es opaco (estilo ApiKey). El refresh es POST /token.
// VERIFICADO EN VIVO (2026-06-25) y en el código fuente (AuthenticationService.token):
//   body = { username, accessToken, requestToken }  (los TRES campos, no solo requestToken)
//   respuesta = { accessToken, requestToken }  (ambos rotan; el accessToken viejo queda inválido)
let refreshPromise = null

async function refreshToken() {
  const auth = getStoredAuth()
  if (!auth?.requestToken || !auth?.username) throw new Error('sin requestToken/username para refresh')
  // axios "crudo" para no recursar sobre el interceptor
  const { data } = await axios.post(
    `${api.defaults.baseURL}/token`,
    { username: auth.username, accessToken: auth.accessToken, requestToken: auth.requestToken },
    { headers: { Authorization: `Bearer ${auth.accessToken}` } },
  )
  const next = {
    ...auth,
    accessToken: data.accessToken || data.token || auth.accessToken,
    // el requestToken también rota; conservamos el viejo solo como fallback defensivo
    requestToken: data.requestToken || auth.requestToken,
  }
  setStoredAuth(next)
  applyAuthHeaders(next) // re-aplica Authorization + X-Access (locacionId persiste en auth)
  return next
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {}
    const status = error.response?.status
    const isAuthCall = (original.url || '').includes('/login') || (original.url || '').includes('/token')
    if (status === 401 && !original.__retried && !isAuthCall) {
      original.__retried = true
      try {
        if (!refreshPromise) refreshPromise = refreshToken().finally(() => { refreshPromise = null })
        const next = await refreshPromise
        original.headers = { ...original.headers, Authorization: `Bearer ${next.accessToken}` }
        return api(original)
      } catch (e) {
        clearStoredAuth()
        applyAuthHeaders(null)
        if (onForcedLogout) onForcedLogout()
        return Promise.reject(e)
      }
    }
    return Promise.reject(error)
  },
)
